import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import { ArrowLeft } from 'lucide-react';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    phone: '',
    specialty: '',
    collegiate_number: '',
    official_college: '',
    country: 'España',
    areas_to_certify: '',
    society_group: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const areas = formData.areas_to_certify.split(',').map(a => a.trim()).filter(a => a);

    const userData = {
      email: formData.email,
      password: '',
      full_name: formData.full_name,
      phone: formData.phone,
      specialty: formData.specialty,
      collegiate_number: formData.collegiate_number || null,
      official_college: formData.official_college || null,
      country: formData.country,
      areas_to_certify: areas,
      society_group: formData.society_group || null,
    };

    const result = await register(userData);
    setLoading(false);

    if (result.success) {
      toast({
        title: "Registro exitoso",
        description: "Hemos enviado sus credenciales de acceso a su correo electrónico.",
      });
      navigate('/login');
    } else {
      toast({
        title: "Error",
        description: result.error,
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen py-12 px-4" style={{ background: '#F6F5F0' }}>
      <div className="container mx-auto max-w-2xl">
        <Link to="/" className="inline-flex items-center mb-6" style={{ color: '#0B1D3A', fontWeight: 500 }}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al inicio
        </Link>

        <Card style={{ borderColor: '#E2DDD3' }}>
          <CardHeader>
            <CardTitle className="text-3xl text-center" style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#0B1D3A' }}>Registro Gratuito</CardTitle>
            <CardDescription className="text-center">
              Complete sus datos. Recibirá sus credenciales de acceso por correo electrónico.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Nombre Completo *</Label>
                    <Input id="full_name" name="full_name" type="text" required value={formData.full_name} onChange={handleChange} placeholder="Dr. Juan Pérez" data-testid="register-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} placeholder="juan@ejemplo.com" data-testid="register-email" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Teléfono *</Label>
                    <Input id="phone" name="phone" type="tel" required value={formData.phone} onChange={handleChange} placeholder="+34 600 000 000" data-testid="register-phone" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="specialty">Especialidad *</Label>
                    <Input id="specialty" name="specialty" type="text" required value={formData.specialty} onChange={handleChange} placeholder="Cirugía Estética, Cardiología..." data-testid="register-specialty" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="collegiate_number">Número de Colegiado</Label>
                    <Input id="collegiate_number" name="collegiate_number" type="text" value={formData.collegiate_number} onChange={handleChange} placeholder="Opcional" data-testid="register-collegiate" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="official_college">Colegio Oficial</Label>
                    <Input id="official_college" name="official_college" type="text" value={formData.official_college} onChange={handleChange} placeholder="Colegio Oficial de Médicos de..." data-testid="register-college" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">País *</Label>
                  <Input id="country" name="country" type="text" required value={formData.country} onChange={handleChange} placeholder="España" data-testid="register-country" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="areas_to_certify">Áreas de Competencia a Certificar *</Label>
                  <Input id="areas_to_certify" name="areas_to_certify" type="text" required value={formData.areas_to_certify} onChange={handleChange} placeholder="Separe con comas: Cirugía Estética, Medicina Estética..." data-testid="register-areas" />
                  <p className="text-sm text-gray-500">Indique las áreas en las que desea obtener certificación</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="society_group">Sociedad / Grupo profesional</Label>
                  <Input id="society_group" name="society_group" type="text" value={formData.society_group} onChange={handleChange} placeholder="Ej: SECPRE, AECEP, SEME..." data-testid="register-society" />
                  <p className="text-sm text-gray-500">Si pertenece a una sociedad o grupo profesional, indiquelo para acceder a descuentos colectivos</p>
                </div>
              </div>

              <div className="p-3 rounded-md text-sm" style={{ background: 'rgba(197,165,90,0.08)', border: '1px solid rgba(197,165,90,0.2)', color: '#4A4A5A' }}>
                Su contraseña será generada automáticamente y enviada a su correo electrónico. Podrá solicitar una nueva contraseña en cualquier momento.
              </div>

              <Button type="submit" className="w-full" style={{ background: '#C5A55A', color: '#0B1D3A', fontWeight: 700 }} disabled={loading} data-testid="register-submit-btn">
                {loading ? 'Registrando...' : 'Crear Cuenta Gratuita'}
              </Button>

              <p className="text-center text-sm text-gray-600">
                ¿Ya tiene una cuenta?{' '}
                <Link to="/login" style={{ color: '#C5A55A', fontWeight: 600 }} className="hover:underline">Iniciar sesión</Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;
